import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { api, apiErrorMessage } from '../lib/api-client';
import { Button, modalFormCls, NATIVE_DIALOG_CLS, TextAction } from '@payforge/ui';
import { CheckIcon, CloseIcon } from './icons';
import { formatDateRange } from '../lib/dates';
import { openChangeRevision } from '../lib/pendingChange';
import { dayCount, hasExcludedWeekday, listedDaysOf, sideText } from '../lib/revisionText';
import { useRequestRevisions } from '../lib/useRequestRevisions';
import { PendingChangeSummary } from './PendingChangeSummary';
import { RequestTimeline } from './RequestTimeline';
import { TimeOffAttachmentList } from './TimeOffAttachmentList';
import type { TimeOffDecisionCommand, TimeOffRequestDto } from '../lib/types';

// Approve/Deny a pending time-off request. POSTs to the decision endpoint; comment
// is optional (the backend seeds a default "Approved."/"Denied." when blank).
// Shared by Approvals page and home widget.
export function TimeOffDecisionDialog({
  request,
  decision,
  onClose,
  onDecided,
}: {
  request: TimeOffRequestDto;
  decision: 'APPROVE' | 'DENY';
  onClose: () => void;
  onDecided: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyId = useId();
  // A change is decided on what it changes, whose runs and removed days only its history holds, so
  // its dialog reads the history on open and Show history shares that read. So does a new request
  // with an excluded weekday, which may skip a working day that only the history can tell from a
  // holiday; any other new request reads nothing until Show history. A refused or failed read leaves
  // the summary on the request's own values, with no error: the decision does not depend on it.
  const isChange = request.status === 'PENDING_CHANGE';
  const mayListDays = request.status === 'PENDING' && hasExcludedWeekday(request);
  const revisionsQuery = useRequestRevisions(request.id, { enabled: isChange || mayListDays });
  const listedDays = mayListDays && revisionsQuery.isSuccess ? listedDaysOf(revisionsQuery.data) : null;

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  // The list-view request may not carry attachments; the single-request detail read does. Fetch it
  // so an approver can review any files attached to the request before deciding.
  const detailQuery = useQuery({
    queryKey: ['time-off', 'request', request.id],
    queryFn: () => api.get<TimeOffRequestDto>(`/v1/time-off/requests/${request.id}`),
  });
  const attachments = detailQuery.data?.attachments ?? request.attachments ?? [];

  const decisionMutation = useMutation({
    mutationFn: (cmd: TimeOffDecisionCommand) =>
      api.post<TimeOffRequestDto>(`/v1/time-off/requests/${request.id}/decision`, cmd),
    onSuccess: () => onDecided(),
    onError: (err) => setError(apiErrorMessage(err, 'Failed to record decision.')),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const comment = String(form.get('comment') ?? '').trim();
    setError(null);
    decisionMutation.mutate({ decision, comment: comment || undefined });
  }

  const action = decision === 'APPROVE' ? 'Approve' : 'Deny';

  return (
    <dialog ref={dialogRef} onClose={onClose} className={NATIVE_DIALOG_CLS}>
      <form onSubmit={handleSubmit} className={modalFormCls('w-[min(24rem,95vw)]')}>
        <h2 className="text-lg font-semibold text-slate-900">
          {action} request #{request.id}
        </h2>
        {isChange ? (
          <PendingChangeSummary
            request={request}
            revision={revisionsQuery.isSuccess ? openChangeRevision(revisionsQuery.data) : null}
            className="mt-2"
          />
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            {listedDays
              ? sideText(listedDays)
              : `${formatDateRange(request.startDate, request.endDate)}, ${dayCount(Number(request.days))}`}
          </p>
        )}

        <div className="mt-2">
          <TextAction
            aria-expanded={historyOpen}
            aria-controls={historyOpen ? historyId : undefined}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            {historyOpen ? 'Hide history' : 'Show history'}
          </TextAction>
        </div>
        {historyOpen ? (
          // Focusable so a keyboard user can scroll a long history inside the dialog.
          <div
            id={historyId}
            role="region"
            aria-label="Request history"
            tabIndex={0}
            className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 p-3"
          >
            <RequestTimeline requestId={request.id} compact />
          </div>
        ) : null}

        <TimeOffAttachmentList requestId={request.id} attachments={attachments} className="mt-4" />

        <label className="mt-4 block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Comment <span className="text-slate-400">(optional)</span>
          </span>
          <textarea
            name="comment"
            maxLength={1024}
            rows={3}
            placeholder="Optional"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
        </label>

        {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button size="md" variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="md"
            type="submit"
            variant={decision === 'APPROVE' ? 'success' : 'danger'}
            disabled={decisionMutation.isPending}
            icon={
              decision === 'APPROVE'
                ? <CheckIcon className="h-4 w-4" />
                : <CloseIcon className="h-4 w-4" />
            }
          >
            {decisionMutation.isPending ? 'Submitting…' : action}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
